import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { createBrowserRouter, Outlet, RouterProvider, useParams } from 'react-router-dom'
import { Provider as StoreProvider } from 'react-redux'
import Page from './components/Page/Page'
import PasswordResetPage from './pages/PasswordResetPage/PasswordResetPage'

import { EntityContextProvider } from './state/entities'
import { BrandingProvider } from './branding'
import TranslationProvider from './translations/TranslationProvider'
import { DialogProvider } from './utilities/useDialog'
import LoginGuard from './components/LoginGuard/LoginGuard'
import ErrorPage from './pages/ErrorPage/ErrorPage'
import { reactRouterRoutes } from './routes'
import { SessionProvider } from './state/session'
import store from './state/store'

import './main.module.scss'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

const Language = React.memo(function Language() {
  const { i18n } = useTranslation()
  const navigate = useNavigate()

  const { lang } = useParams()
  useEffect(() => {
    if (lang !== i18n.language) {
      if (!i18n.options.resources![lang!]) {
        navigate('/en')
      } else {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, navigate])

  return <Outlet />
})

const NotFound = () => {
  throw new Error('Not found')
}

const router = createBrowserRouter([
  {
    path: '/:lang',
    element: <Language />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <DialogProvider />,
        children: [
          {
            element: <LoginGuard />,
            children: [
              {
                element: <Page />,
                children: reactRouterRoutes,
              },
            ],
          },
          {
            path: 'reset-password',
            element: <PasswordResetPage />,
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
    ],
  },
])

async function main() {
  const container = document.getElementById('react-root')!
  const root = createRoot(container)
  root.render(
    <BrandingProvider>
      <QueryClientProvider client={queryClient}>
        <StoreProvider store={store}>
          <EntityContextProvider>
            <SessionProvider>
              <TranslationProvider>
                <RouterProvider router={router} />
              </TranslationProvider>
            </SessionProvider>
          </EntityContextProvider>
        </StoreProvider>
      </QueryClientProvider>
    </BrandingProvider>
  )
}

main()
